import { Link, useLocation } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { GitHubIntegration } from "@/components/settings/github-integration";
import { LocalRunnerIntegration } from "@/components/settings/local-runner-integration";
import { JiraIntegration } from "@/components/settings/jira-integration";
import { AzureDevOpsIntegration } from "@/components/settings/azure-devops-integration";
import { LinearIntegration } from "@/components/settings/linear-integration";
import { SyncWorkflowGuide } from "@/components/settings/sync-workflow-guide";

function buildConfigs(): Record<string, { label: string; config: string }> {
  const apiUrl = window.location.origin;
  const envBlock = `      "env": {
        "IAMU_API_URL": "${apiUrl}",
        "IAMU_API_KEY": "<your-access-token>",
        "IAMU_PROJECT_ID": "<your-project-id>"
      }`;

  return {
    claude: {
      label: "Claude Code",
      config: `// .mcp.json
{
  "mcpServers": {
    "iamu-sync": {
      "command": "npx",
      "args": ["-y", "@iamu-dev/iamu", "sync-server"],
${envBlock}
    }
  }
}`,
    },
    cursor: {
      label: "Cursor",
      config: `// .cursor/mcp.json
{
  "mcpServers": {
    "iamu-sync": {
      "command": "npx",
      "args": ["-y", "@iamu-dev/iamu", "sync-server"],
${envBlock}
    }
  }
}`,
    },
    windsurf: {
      label: "Windsurf",
      config: `// .windsurf/mcp.json
{
  "mcpServers": {
    "iamu-sync": {
      "command": "npx",
      "args": ["-y", "@iamu-dev/iamu", "sync-server"],
${envBlock}
    }
  }
}`,
    },
    generic: {
      label: "Generic MCP",
      config: `// Any MCP-compatible client
{
  "mcpServers": {
    "iamu-sync": {
      "command": "npx",
      "args": ["-y", "@iamu-dev/iamu", "sync-server"],
${envBlock}
    }
  }
}`,
    },
  };
}

function ConnectionSetup() {
  const configs = buildConfigs();
  const location = useLocation();

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-md">Connection setup</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg bg-primary/5 p-3">
          <p className="text-sm text-foreground">
            You need an access token to authenticate the MCP server.{" "}
            <Link
              to="/settings?tab=access-tokens"
              onClick={() => {
                if (location.pathname === "/settings") {
                  document
                    .getElementById("section-access-tokens")
                    ?.scrollIntoView({ behavior: "instant", block: "start" });
                }
              }}
              className="font-medium underline underline-offset-4"
            >
              Create one in Access tokens
            </Link>{" "}
            — it will include a ready-to-paste config snippet.
          </p>
        </div>

        <Tabs defaultValue="claude">
          <TabsList variant="line">
            {Object.entries(configs).map(([key, { label }]) => (
              <TabsTrigger key={key} value={key}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(configs).map(([key, { config }]) => (
            <TabsContent key={key} value={key} className="pt-3">
              <div className="relative">
                <pre className="rounded-md bg-muted p-4 text-xs overflow-x-auto">
                  {config}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={() => handleCopy(config)}
                  aria-label="Copy configuration"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export function IntegrationContent() {
  return (
    <div className="space-y-6">
      <SyncWorkflowGuide />
      <ConnectionSetup />
      <LocalRunnerIntegration />
      <GitHubIntegration />
      <JiraIntegration />
      <AzureDevOpsIntegration />
      <LinearIntegration />
    </div>
  );
}

export function IntegrationPage() {
  return (
    <Page
      title="Integration"
      description="Connect AI agents and monitor their performance"
    >
      <IntegrationContent />
    </Page>
  );
}
